/**
 * pages/SignInOtp.tsx — OTP step of the web sign-in flow.
 *
 * Step 2 of /signin → /signin/otp. Reads the challenge state from
 * either `location.state` (typical flow) or sessionStorage (hard
 * refresh recovery). If neither is present, redirect to /signin.
 *
 * States it renders explicitly:
 *
 *   * waiting              — clean form, "we sent it to +… tap the
 *                            code below." Resend button disabled with
 *                            a live countdown until `resend_available_at`.
 *   * wrong-code, N left   — inline error inside the 6-box row (shake
 *                            animation), "3 attempts left" copy.
 *   * exhausted            — 3 wrong codes destroyed the challenge;
 *                            "Start over" is the only action.
 *   * expired              — challenge past its 5-min TTL; same
 *                            "Start over" action.
 *   * signup_required      — OTP was correct but the phone has no
 *                            user record. Route to /signup once it
 *                            ships; for now show a coming-soon panel
 *                            (rendered inline, not a full redirect,
 *                            so the user sees their number confirmed).
 *   * verifying / signing_in — network pending during verify / cookie mint
 *
 * The whole page is a single component with an internal state machine
 * (rather than a nested router) because the transitions are dense
 * and share the same visual shell.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Button, ThemeToggle } from "../components";
import { OtpInput, OTP_LENGTH } from "../components/OtpInput";
import {
  createWebSessionFromOtp,
  createWebSessionFromSignup,
  resendPhoneOtp,
  verifyPhoneOtp,
} from "../api/session";
import { useAuth } from "../auth/AuthContext";
import { ApiClientError } from "../api/client";
import { SIGNIN_OTP_STATE_KEY, type SignInOtpState } from "./SignIn";
import styles from "./SignInOtp.module.css";

type UiState =
  | { kind: "waiting" }
  | { kind: "verifying" }
  | { kind: "wrong_code"; attempts_remaining: number }
  | { kind: "exhausted" }
  | { kind: "expired" }
  | { kind: "signing_in" }
  | { kind: "signup_required" }
  | { kind: "error"; message: string };

/** Recover the challenge state from location.state OR sessionStorage. */
function useOtpState(): SignInOtpState | null {
  const location = useLocation();
  return useMemo(() => {
    const fromNav = (location.state as SignInOtpState | null) ?? null;
    if (fromNav && fromNav.challenge_id) return fromNav;
    try {
      const raw = sessionStorage.getItem(SIGNIN_OTP_STATE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as SignInOtpState;
      if (!parsed?.challenge_id) return null;
      return parsed;
    } catch {
      return null;
    }
  }, [location.state]);
}

/** Live countdown in seconds until `iso`; clamps to 0. */
function useSecondsUntil(iso: string | undefined): number {
  const [secs, setSecs] = useState(() => (iso ? Math.max(0, Math.floor((new Date(iso).getTime() - Date.now()) / 1000)) : 0));
  useEffect(() => {
    if (!iso) return undefined;
    const tick = () => {
      const s = Math.max(0, Math.floor((new Date(iso).getTime() - Date.now()) / 1000));
      setSecs(s);
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [iso]);
  return secs;
}

export function SignInOtp() {
  const { status, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const initialState = useOtpState();

  // Route-derived mode. /signin/otp → sign-in (existing user);
  // /signup/otp → signup (create user). Determines which endpoint
  // consumes the verification_token on success and where to route
  // the freshly-authed user next.
  const mode: "signin" | "signup" = location.pathname.startsWith("/signup")
    ? "signup"
    : "signin";

  // Local shadow of the challenge state — /resend replaces it in place.
  const [challenge, setChallenge] = useState<SignInOtpState | null>(initialState);
  const [code, setCode] = useState("");
  const [ui, setUi] = useState<UiState>({ kind: "waiting" });
  const [resendPending, setResendPending] = useState(false);

  // ---------------------------------------------------------------------
  // HOOKS BLOCK — every hook this component uses lives ABOVE the two
  // early-return branches below. React tracks hook order by call
  // index; if the same component instance renders once with N hooks
  // and later renders with < N (because an early return skipped some),
  // React throws "Rendered fewer hooks than expected."
  //
  // The specific crash this ordering prevents:
  //   Successful OTP verify → refresh() → AuthContext status flips
  //   to "signed_in" → next render hits `if (status === "signed_in")`
  //   and returns <Navigate/> BEFORE reaching the hooks that ran on
  //   the previous render → hook-count mismatch → React error →
  //   the app (no ErrorBoundary in the tree, so root goes empty →
  //   the dark canvas background shows) → BLACK SCREEN.
  //
  // ANY new hook added to this component MUST be added above the
  // early returns. Do not fold "cheap" hooks below on the theory
  // that the branches are near-instant — the whole point is
  // determinism across renders, not speed.
  // ---------------------------------------------------------------------

  // Live countdowns. useSecondsUntil() safely accepts `undefined` when
  // `challenge` is null on the pre-redirect render.
  const resendSecs = useSecondsUntil(challenge?.resend_available_at);
  const expirySecs = useSecondsUntil(challenge?.expires_at);

  // Once the challenge itself expires (past `expires_at`), lock the
  // UI into the `expired` state. Server-side rejects a late verify
  // anyway; this avoids the round trip and shows the correct copy.
  //
  // Guard: only fire the transition once we actually have a challenge
  // to expire. Without this, mounting with challenge=null (the
  // pre-redirect render) would set ui=expired and then still redirect,
  // which is a no-op but noisy.
  useEffect(() => {
    if (!challenge) return;
    if (expirySecs === 0 && ui.kind !== "exhausted" && ui.kind !== "expired"
        && ui.kind !== "signup_required" && ui.kind !== "signing_in") {
      setUi({ kind: "expired" });
    }
  }, [challenge, expirySecs, ui.kind]);

  const doVerify = useCallback(async (finalCode: string) => {
    // Defensive: challenge is null on the pre-redirect render.
    // The OtpInput is disabled in that case (inputDisabled is true
    // when isFailure branches, and we don't render the input at all
    // when challenge is null — see below), so this shouldn't fire.
    // But belt-and-braces: bail if it does.
    if (!challenge) return;
    setUi({ kind: "verifying" });
    let verifyResult;
    try {
      verifyResult = await verifyPhoneOtp(challenge.challenge_id, finalCode);
    } catch (e) {
      setUi({
        kind: "error",
        message:
          e instanceof ApiClientError
            ? e.envelope.message || "Couldn't verify the code. Try again."
            : "Couldn't reach AsterCash. Check your connection.",
      });
      return;
    }

    if (verifyResult.kind === "success") {
      setUi({ kind: "signing_in" });
      // Signup mode calls a different endpoint that creates the user
      // row on the way to minting the session. Both consume the
      // verification_token; both mint the same session cookies.
      if (mode === "signup") {
        const result = await createWebSessionFromSignup(
          verifyResult.verification_token,
        );
        if (result.kind === "success") {
          sessionStorage.removeItem(SIGNIN_OTP_STATE_KEY);
          await refresh();
          // Route to the first signup screen. Legal disclosures must
          // be accepted before any CIP data is submitted.
          navigate("/signup/legal");
          return;
        }
        setUi({ kind: "expired" });
        return;
      }
      const result = await createWebSessionFromOtp(
        verifyResult.verification_token,
      );
      if (result.kind === "success") {
        // Session cookies are now set. Refresh the AuthContext so
        // it flips to signed_in and the router bounces to /.
        //
        // NB: after this awaits, the AuthContext status transitions
        // to "signed_in" on the NEXT render. The early-return check
        // at the top of the return block (see below) picks that up
        // and Navigates to "/". Because all hooks are declared here
        // (above the early returns), the hook count doesn't change
        // across the transition — no "fewer hooks than expected"
        // crash. See HOOKS BLOCK comment.
        sessionStorage.removeItem(SIGNIN_OTP_STATE_KEY);
        await refresh();
        return;
      }
      if (result.kind === "signup_required") {
        sessionStorage.removeItem(SIGNIN_OTP_STATE_KEY);
        setUi({ kind: "signup_required" });
        return;
      }
      // token_invalid — the verification_token expired between
      // /phone/verify and /web/session/otp (should be sub-second in
      // practice). Restart the flow.
      setUi({ kind: "expired" });
      return;
    }
    if (verifyResult.kind === "wrong_code") {
      setCode("");
      if (verifyResult.attempts_remaining === 0) {
        setUi({ kind: "exhausted" });
      } else {
        setUi({
          kind: "wrong_code",
          attempts_remaining: verifyResult.attempts_remaining,
        });
      }
      return;
    }
    if (verifyResult.kind === "consumed" || verifyResult.kind === "expired") {
      setUi({ kind: "expired" });
      return;
    }
    if (verifyResult.kind === "rate_limited") {
      setUi({
        kind: "error",
        message: `Too many attempts. Try again in ${Math.ceil(
          verifyResult.retry_after_seconds / 60,
        )} minute(s).`,
      });
      return;
    }
    // 'not_delivered' — SignalWire couldn't deliver. Give the user
    // an actionable message rather than a generic error.
    setUi({
      kind: "error",
      message:
        "We couldn't deliver the code by SMS. Check your number and try Resend.",
    });
  }, [challenge, mode, navigate, refresh]);

  // Auto-submit once all six digits are entered. The OtpInput's
  // onComplete fires only on the exact-6 threshold.
  const submittedRef = useRef<string | null>(null);
  const onComplete = useCallback((c: string) => {
    if (submittedRef.current === c) return;   // idempotent guard
    submittedRef.current = c;
    void doVerify(c);
  }, [doVerify]);

  // ---------------------------------------------------------------------
  // Early-return branches. These MUST live AFTER every hook call —
  // see HOOKS BLOCK comment above for why.
  // ---------------------------------------------------------------------

  // If the AuthContext booted us into signed_in mid-flight (e.g. the
  // web-session cookie was set by a concurrent tab, OR the OTP verify
  // just completed successfully and refresh() flipped the status),
  // don't force the user to redo the OTP. Bounce to home.
  if (status === "signed_in") {
    return <Navigate to="/" replace />;
  }

  // No challenge state at all → send back to /signin. This is the
  // hard-refresh recovery guard.
  if (!challenge) {
    return <Navigate to="/signin" replace />;
  }

  async function doResend() {
    // TS: `challenge` is component-state and typed `| null`; the top-of-
    // component guard already redirects when it's null. Narrow locally
    // so the spread below doesn't infer `phone_e164?: string`.
    if (!challenge) return;
    setResendPending(true);
    try {
      const fresh = await resendPhoneOtp(challenge.challenge_id);
      const next: SignInOtpState = {
        ...challenge,
        challenge_id: fresh.challenge_id,
        attempts_remaining: fresh.attempts_remaining,
        expires_at: fresh.expires_at,
        resend_available_at: fresh.resend_available_at,
        started_at: new Date().toISOString(),
      };
      setChallenge(next);
      try {
        sessionStorage.setItem(SIGNIN_OTP_STATE_KEY, JSON.stringify(next));
      } catch {
        /* private-mode Safari */
      }
      setCode("");
      submittedRef.current = null;
      setUi({ kind: "waiting" });
    } catch (e) {
      setUi({
        kind: "error",
        message:
          e instanceof ApiClientError
            ? e.envelope.message || "Couldn't resend the code."
            : "Couldn't reach AsterCash. Check your connection.",
      });
    } finally {
      setResendPending(false);
    }
  }

  function startOver() {
    sessionStorage.removeItem(SIGNIN_OTP_STATE_KEY);
    setCode("");
    submittedRef.current = null;
    navigate("/signin", { replace: true });
  }

  const isFailure =
    ui.kind === "exhausted" || ui.kind === "expired" ||
    ui.kind === "signup_required" || ui.kind === "error";
  const inputDisabled =
    ui.kind === "verifying" || ui.kind === "signing_in" || isFailure;
  const errored =
    ui.kind === "wrong_code" || ui.kind === "expired" || ui.kind === "exhausted";

  return (
    <main className={styles.page}>
      <ThemeToggle />
      <section className={styles.card}>
        <button
          type="button"
          className={styles.backLink}
          onClick={startOver}
          aria-label="Back to phone number"
        >
          ← Change number
        </button>

        <h1 className={styles.title}>Enter your code</h1>
        <p className={styles.body}>
          We sent a 6-digit code to <strong>{challenge.phone_e164}</strong>.
          It expires in {Math.ceil(expirySecs / 60)} min.
        </p>

        <OtpInput
          value={code}
          onChange={setCode}
          onComplete={onComplete}
          disabled={inputDisabled}
          error={errored}
          autoFocus
        />

        {/* --------- Status line below the boxes --------- */}
        {ui.kind === "waiting" && (
          <p className={styles.statusLine} aria-live="polite">
            Enter all {OTP_LENGTH} digits to continue.
          </p>
        )}
        {ui.kind === "verifying" && (
          <p className={styles.statusLine} aria-live="polite">
            Checking code…
          </p>
        )}
        {ui.kind === "signing_in" && (
          <p className={styles.statusLine} aria-live="polite">
            Signing you in…
          </p>
        )}
        {ui.kind === "wrong_code" && (
          <p className={styles.errorLine} role="alert">
            Wrong code. {ui.attempts_remaining}{" "}
            {ui.attempts_remaining === 1 ? "attempt" : "attempts"} left.
          </p>
        )}
        {ui.kind === "exhausted" && (
          <p className={styles.errorLine} role="alert">
            Too many wrong attempts. Start over to receive a new code.
          </p>
        )}
        {ui.kind === "expired" && (
          <p className={styles.errorLine} role="alert">
            This code expired. Resend a new one or change your number.
          </p>
        )}
        {ui.kind === "error" && (
          <p className={styles.errorLine} role="alert">{ui.message}</p>
        )}
        {ui.kind === "signup_required" && (
          <div className={styles.signupPanel} role="alert">
            <p className={styles.signupTitle}>
              No AsterCash account for {challenge.phone_e164}
            </p>
            <p className={styles.signupBody}>
              Your phone is confirmed — the next step is to open your
              AsterCash account. This takes about four minutes.
            </p>
            <Button
              variant="primary"
              fullWidth
              onClick={() => {
                sessionStorage.removeItem(SIGNIN_OTP_STATE_KEY);
                navigate("/signup");
              }}
            >
              Open my account
            </Button>
            <Button variant="secondary" fullWidth onClick={startOver}>
              Use a different number
            </Button>
          </div>
        )}

        {/* --------- Resend / start-over row --------- */}
        {(ui.kind === "waiting" ||
          ui.kind === "wrong_code" ||
          ui.kind === "expired" ||
          ui.kind === "error") && (
          <div className={styles.actionsRow}>
            <button
              type="button"
              className={styles.resendBtn}
              onClick={doResend}
              disabled={resendPending || resendSecs > 0}
            >
              {resendPending
                ? "Sending…"
                : resendSecs > 0
                ? `Resend in ${resendSecs}s`
                : "Resend code"}
            </button>
          </div>
        )}

        {ui.kind === "exhausted" && (
          <div className={styles.actionsRow}>
            <Button variant="primary" fullWidth onClick={startOver}>
              Start over
            </Button>
          </div>
        )}
      </section>
    </main>
  );
}
